/**
 * SupplierCache - Client-side caching for supplier details
 *
 * Provides a simple in-memory cache for supplier data with time-based expiration.
 * This helps reduce API calls and improve performance when viewing supplier details.
 */

class SupplierCache {
  static cache = new Map();
  static cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * Get cached data for a supplier
   * @param {number} id - Supplier ID
   * @returns {Object|null} - Cached data or null if not found/expired
   */
  static get(id) {
    const cached = this.cache.get(id);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  /**
   * Store data in cache for a supplier
   * @param {number} id - Supplier ID
   * @param {Object} data - Data to cache
   */
  static set(id, data) {
    this.cache.set(id, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Remove a specific supplier from cache
   * @param {number} id - Supplier ID
   */
  static remove(id) {
    this.cache.delete(id);
  }

  /**
   * Clear all cached data
   */
  static clear() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache statistics
   */
  static getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [, cached] of this.cache.entries()) {
      if (now - cached.timestamp < this.cacheTimeout) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      cacheTimeout: this.cacheTimeout,
    };
  }

  /**
   * Remove expired entries from cache
   */
  static cleanup() {
    const now = Date.now();
    for (const [id, cached] of this.cache.entries()) {
      if (now - cached.timestamp >= this.cacheTimeout) {
        this.cache.delete(id);
      }
    }
  }
}

// Plain object for supplier select list cache (used in dropdowns)
const globalSupplierSelectCache = {
  data: null,
  isFetching: false,
  promise: null,
  timestamp: null, // When cache was last populated
  invalidatedAt: null, // When cache was last invalidated
};

// Cache expiration time: 1 minute
const CACHE_EXPIRATION_TIME = 60 * 1000; // 1 minute in milliseconds

// Timestamp when product select list was last invalidated (e.g. after Excel upload created products)
let productSelectInvalidatedAt = null;

/**
 * Invalidates the product select list cache.
 * Forces QuoteModal (and any component using per-supplier product list) to refetch products
 * when opening a quote for edit. Call after creating suppliers/products via Excel/PDF upload.
 */
export const invalidateProductSelectCache = () => {
  productSelectInvalidatedAt = Date.now();
};

/**
 * Returns the timestamp when product select was last invalidated, or null.
 * Used by QuoteModal to clear fetchedSupplierProductsRef so product list is refetched.
 */
export const getProductSelectInvalidatedAt = () => productSelectInvalidatedAt;

/**
 * Invalidates the supplier select list cache.
 * This forces components to fetch a fresh supplier list on next access.
 * Useful for refreshing the cache after suppliers are created/updated externally.
 */
export const invalidateSupplierSelectCache = () => {
  globalSupplierSelectCache.data = null;
  globalSupplierSelectCache.promise = null;
  globalSupplierSelectCache.timestamp = null;
  globalSupplierSelectCache.isFetching = false; // Clear any in-progress fetches
  globalSupplierSelectCache.invalidatedAt = Date.now();
};

/**
 * Returns true when the supplier select cache was invalidated and has not yet been repopulated.
 * Use this to force a refetch instead of using a possibly stale prop (e.g. in QuoteModal when editing after Excel/PDF upload).
 * @returns {boolean}
 */
export const isSupplierSelectCacheInvalidated = () => {
  if (!globalSupplierSelectCache.invalidatedAt) return false;
  if (!globalSupplierSelectCache.data || !globalSupplierSelectCache.timestamp) return true;
  return globalSupplierSelectCache.invalidatedAt > globalSupplierSelectCache.timestamp;
};

/**
 * Checks if the cache is stale (needs refresh).
 * Cache is considered stale if:
 * 1. It has no timestamp (was never properly set or was cleared), OR
 * 2. It was invalidated after the last load time, OR
 * 3. It's older than 1 minute (CACHE_EXPIRATION_TIME)
 * @param {number} lastLoadTime - Timestamp when component last loaded from cache
 * @returns {boolean} - True if cache is stale and needs refresh
 */
export const isCacheStale = lastLoadTime => {
  const now = Date.now();

  // If no timestamp, treat as stale (cache was never properly set or was cleared)
  if (!globalSupplierSelectCache.timestamp) {
    return true;
  }

  // Check if cache was invalidated after last load
  if (globalSupplierSelectCache.invalidatedAt) {
    if (!lastLoadTime) {
      return true; // No previous load, treat as stale
    }
    if (globalSupplierSelectCache.invalidatedAt > lastLoadTime) {
      return true; // Was invalidated after last load
    }
  }

  // Check if cache is older than expiration time
  const cacheAge = now - globalSupplierSelectCache.timestamp;
  if (cacheAge > CACHE_EXPIRATION_TIME) {
    return true; // Cache is too old
  }

  return false; // Cache is still fresh
};

/**
 * Expose cache utilities to window for production debugging
 * Usage in console: window.refreshSupplierCache()
 */
if (typeof window !== 'undefined') {
  window.refreshSupplierCache = invalidateSupplierSelectCache;
  window.getSupplierCacheInfo = () => ({
    hasData: !!globalSupplierSelectCache.data,
    cacheLength: globalSupplierSelectCache.data?.length || 0,
    supplierIds:
      globalSupplierSelectCache.data?.map(s => s.value || s.id) || [],
    isFetching: globalSupplierSelectCache.isFetching,
    timestamp: globalSupplierSelectCache.timestamp,
    invalidatedAt: globalSupplierSelectCache.invalidatedAt,
  });
}

export default globalSupplierSelectCache;
export { SupplierCache };
